<template>
    <div class="sl-page">
        <!-- header -->
        <div class="sl-head">
            <div class="sl-title-wrap">
                <span class="sl-title-ic"><v-icon small>mdi-clipboard-text-multiple-outline</v-icon></span>
                <h1 class="sl-title">{{ $t('Surveys') }}</h1>
                <span class="sl-count">{{ filteredList.length }}</span>
            </div>
            <router-link :to="{ name: 'add-survey' }" custom v-slot="{ navigate }">
                <v-btn class="sl-add" depressed color="primary" role="link" @click="navigate">
                    <v-icon small class="me-1">mdi-plus</v-icon>{{ $t('AddSurvey') }}
                </v-btn>
            </router-link>
        </div>

        <v-card flat class="sl-card" :loading="loading">
            <!-- toolbar: search + filter chips -->
            <div class="sl-toolbar">
                <div class="sl-search">
                    <v-icon small class="sl-search-ic">mdi-magnify</v-icon>
                    <input v-model="search" type="search" class="sl-search-input" :placeholder="$t('Search')" :aria-label="$t('Search')" />
                </div>
                <div class="sl-filters" role="group" :aria-label="$t('Filters')">
                    <button type="button" class="sl-filter" :class="{ 'is-on': isPublished === true }" :aria-pressed="isPublished === true ? 'true' : 'false'" @click="isPublished = isPublished === true ? null : true">
                        <v-icon x-small class="sl-filter-ic">mdi-earth</v-icon>{{ $t('Published') }}
                    </button>
                    <button type="button" class="sl-filter" :class="{ 'is-on': active === true }" :aria-pressed="active === true ? 'true' : 'false'" @click="active = active === true ? null : true">
                        <v-icon x-small class="sl-filter-ic">mdi-play-circle-outline</v-icon>{{ $t('Active') }}
                    </button>
                    <button type="button" class="sl-filter" :class="{ 'is-on': showOnHomePage === true }" :aria-pressed="showOnHomePage === true ? 'true' : 'false'" @click="showOnHomePage = showOnHomePage === true ? null : true">
                        <v-icon x-small class="sl-filter-ic">mdi-home-outline</v-icon>{{ $t('ShowOnHomePage') }}
                    </button>
                    <button type="button" class="sl-filter" :class="{ 'is-on': isShared === true }" :aria-pressed="isShared === true ? 'true' : 'false'" @click="isShared = isShared === true ? null : true">
                        <v-icon x-small class="sl-filter-ic">mdi-share-variant-outline</v-icon>{{ $t('IsShared') }}
                    </button>
                </div>
            </div>

            <v-data-table :headers="headers"
                          :items="filteredList"
                          :search="search"
                          class="sl-table"
                          :footer-props="{ itemsPerPageOptions: [10, 25, 50, 100], showFirstLastPage: false }"
                          @dblclick:row="(event, { item }) => editSurvey(item.id)">
                <template v-slot:[`item.id`]="{ item }">
                    <span class="sl-id">{{ item.id }}</span>
                </template>
                <template v-slot:[`item.nameAr`]="{ item }">
                    <span class="sl-name">{{ RemoveHtmlTags(item.nameAr) }}</span>
                </template>
                <template v-slot:[`item.expiryDate`]="{ item }">
                    <span class="sl-date">{{ item.expiryDate | formatDateGregorian }}</span>
                </template>
                <template v-slot:[`item.startDate`]="{ item }">
                    <span class="sl-date">{{ item.startDate | formatDateGregorian }}</span>
                </template>
                <template v-slot:[`item.createdByName`]="{ item }">
                    <span class="sl-muted">{{ item.createdByName }}</span>
                </template>
                <template v-slot:[`item.expired`]="{ item }">
                    <span class="sl-flag" :class="item.expired ? 'is-off' : 'is-on'"><v-icon x-small>{{ item.expired ? 'mdi-minus' : 'mdi-check' }}</v-icon></span>
                </template>
                <template v-slot:[`item.status`]="{ item }">
                    <span class="sl-status" :class="`sl-status--${item.status.toLowerCase()}`"><span class="sl-status-dot" aria-hidden="true"></span>{{ $t(item.status) }}</span>
                </template>
                <template v-slot:[`item.isPublished`]="{ item }">
                    <span class="sl-flag" :class="item.isPublished ? 'is-on' : 'is-off'"><v-icon x-small>{{ item.isPublished ? 'mdi-check' : 'mdi-minus' }}</v-icon></span>
                </template>
                <template v-slot:[`item.hasTrickyQuestion`]="{ item }">
                    <span class="sl-flag" :class="item.hasTrickyQuestion ? 'is-on' : 'is-off'"><v-icon x-small>{{ item.hasTrickyQuestion ? 'mdi-check' : 'mdi-minus' }}</v-icon></span>
                </template>
                <template v-slot:[`item.showOnHomePage`]="{ item }">
                    <span class="sl-flag" :class="item.showOnHomePage ? 'is-on' : 'is-off'"><v-icon x-small>{{ item.showOnHomePage ? 'mdi-check' : 'mdi-minus' }}</v-icon></span>
                </template>
                <template v-slot:[`item.isShared`]="{ item }">
                    <span class="sl-flag" :class="item.isShared ? 'is-on' : 'is-off'"><v-icon x-small>{{ item.isShared ? 'mdi-check' : 'mdi-minus' }}</v-icon></span>
                </template>
                <template v-slot:[`item.hideFromMediaCenter`]="{ item }">
                    <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                            <span class="sl-flag" :class="item.hideFromMediaCenter ? 'is-warn' : 'is-on'" v-bind="attrs" v-on="on">
                                <v-icon x-small>{{ item.hideFromMediaCenter ? 'mdi-eye-off-outline' : 'mdi-check' }}</v-icon>
                            </span>
                        </template>
                        <span>{{ item.hideFromMediaCenter ? $t('HideFromMediaCenter') : $t('MediaCenter') }}</span>
                    </v-tooltip>
                </template>

                <template v-slot:[`item.actions`]="{ item }">
                    <div class="sl-actions">
                        <v-tooltip v-if="item.statusId == approved" top>
                            <template v-slot:activator="{ on, attrs }">
                                <router-link class="sl-action" :to="{ name: 'survey-results', params: { id: item.id } }" v-bind="attrs" v-on="on" :aria-label="$t('Results')">
                                    <v-icon small>mdi-list-box-outline</v-icon>
                                </router-link>
                            </template>
                            <span>{{ $t('Results') }}</span>
                        </v-tooltip>
                        <v-tooltip v-if="item.statusId == approved" top>
                            <template v-slot:activator="{ on, attrs }">
                                <router-link class="sl-action" :to="{ name: 'survey-analytics', params: { id: item.id } }" v-bind="attrs" v-on="on" :aria-label="$t('Analytics')">
                                    <v-icon small>mdi-chart-areaspline</v-icon>
                                </router-link>
                            </template>
                            <span>{{ $t('Analytics') }}</span>
                        </v-tooltip>
                        <v-tooltip top>
                            <template v-slot:activator="{ on, attrs }">
                                <button type="button" class="sl-action" v-bind="attrs" v-on="on" :aria-label="$t('QrCode')" @click="genrateQrCode(item.id,item.isShared,getTitle(item))">
                                    <v-icon small>mdi-qrcode-scan</v-icon>
                                </button>
                            </template>
                            <span>{{ $t('QrCode') }}</span>
                        </v-tooltip>
                        <v-tooltip v-if="item.isPublished" top>
                            <template v-slot:activator="{ on, attrs }">
                                <router-link class="sl-action" :to="{ name: 'share-survey', query: { id: item.id } }" v-bind="attrs" v-on="on" :aria-label="$t('Share')">
                                    <v-icon small>mdi-share-variant-outline</v-icon>
                                </router-link>
                            </template>
                            <span>{{ $t('Share') }}</span>
                        </v-tooltip>
                        <v-tooltip v-if="item.isPublished" top>
                            <template v-slot:activator="{ on, attrs }">
                                <router-link class="sl-action" :to="{ name: 'add-survey', query: { id: item.id } }" v-bind="attrs" v-on="on" :aria-label="$t('View')">
                                    <v-icon small>mdi-eye-outline</v-icon>
                                </router-link>
                            </template>
                            <span>{{ $t('View') }}</span>
                        </v-tooltip>
                        <v-tooltip v-if="item.statusId == draft" top>
                            <template v-slot:activator="{ on, attrs }">
                                <router-link class="sl-action sl-action--primary" :to="{ name: 'add-survey', query: { id: item.id } }" v-bind="attrs" v-on="on" :aria-label="$t('Edit')">
                                    <v-icon small>mdi-pencil-outline</v-icon>
                                </router-link>
                            </template>
                            <span>{{ $t('Edit') }}</span>
                        </v-tooltip>
                        <v-tooltip top>
                            <template v-slot:activator="{ on, attrs }">
                                <button type="button" class="sl-action sl-action--danger" v-bind="attrs" v-on="on" :aria-label="$t('Delete')" @click="removeSurvey(item.id)">
                                    <v-icon small>mdi-trash-can-outline</v-icon>
                                </button>
                            </template>
                            <span>{{ $t('Delete') }}</span>
                        </v-tooltip>
                    </div>
                </template>

                <template v-slot:[`item.options`]="{ item }">
                    <router-link class="mx-3" :to="{ name: 'add-survey', query: { id: item.id }}" v-if="item.statusId == draft">
                        <v-icon color="primary" role="link">mdi-pencil</v-icon>
                    </router-link>
                    <v-icon color="error" @click="removeSurvey(item.id)">mdi-delete</v-icon>
                </template>

                <template v-slot:[`item.menu`]="{ item }">
                    <v-menu offset-y nudge-bottom="4" rounded="lg" content-class="hdr-menu" transition="slide-y-transition">
                        <template v-slot:activator="{ on, attrs }">
                            <button type="button" class="sl-action" v-bind="attrs" v-on="on" aria-label="more">
                                <v-icon small>mdi-dots-horizontal</v-icon>
                            </button>
                        </template>
                        <v-list class="hdr-list" dense>
                            <v-list-item class="hdr-item" @click="copy(item.id)">
                                <span class="hdr-item-ic"><v-icon small>mdi-link-variant</v-icon></span>
                                <v-list-item-title class="hdr-item-title">{{ $t('Copy') }}</v-list-item-title>
                            </v-list-item>
                            <v-list-item class="hdr-item" @click="generateHtml(item.id)">
                                <span class="hdr-item-ic"><v-icon small>mdi-xml</v-icon></span>
                                <v-list-item-title class="hdr-item-title">{{ $t('CopyHTML') }}</v-list-item-title>
                            </v-list-item>
                            <v-list-item class="hdr-item" @click="makeCopy(item.id)">
                                <span class="hdr-item-ic"><v-icon small>mdi-content-duplicate</v-icon></span>
                                <v-list-item-title class="hdr-item-title">{{ $t('MakeCopy') }}</v-list-item-title>
                            </v-list-item>
                            <v-list-item class="hdr-item" @click="exportToEdit(item.id)">
                                <span class="hdr-item-ic"><v-icon small>mdi-microsoft-excel</v-icon></span>
                                <v-list-item-title class="hdr-item-title">{{ $t('EditAnswersInExcel') }}</v-list-item-title>
                            </v-list-item>
                            <div class="hdr-sep"></div>
                            <v-list-item class="hdr-item" @click="toggleMediaCenter(item)">
                                <span class="hdr-item-ic"><v-icon small>{{ item.hideFromMediaCenter ? 'mdi-eye-outline' : 'mdi-eye-off-outline' }}</v-icon></span>
                                <v-list-item-title class="hdr-item-title">{{ item.hideFromMediaCenter ? $t('ShowInMediaCenter') : $t('HideFromMediaCenter') }}</v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </template>

                <template v-slot:no-data>
                    <div class="sl-empty">
                        <v-icon size="36" color="#b8c4c1">mdi-inbox-outline</v-icon>
                        <span>{{ $t('NoItemsToShow') }}</span>
                    </div>
                </template>
                <template v-slot:no-results>
                    <div class="sl-empty">
                        <v-icon size="36" color="#b8c4c1">mdi-magnify-close</v-icon>
                        <span>{{ $t('NoItemsToShow') }}</span>
                    </div>
                </template>
            </v-data-table>

            <ManageQrCode v-model="qrCodeDialog" :surveyURL="surveyURL" :title="selectedTitle" />
            <CommonDialog v-if="showExcelEditor" v-model="showExcelEditor" :fullscreen="false" :title="$t('EditAnswersInExcel')">
                <ExcelEditor v-model="selectedSurveyId"></ExcelEditor>
            </CommonDialog>
        </v-card>
    </div>
</template>

<script lang="ts">
    import ManageQrCode from '@/components/app/admin/survey/ManageQrCode.vue'
    import { mapGetters } from 'vuex'
    import SurveyService from '@/services/SurveyService.js'
    import UsersService from '@/services/UsersService.js'
    import { SurveyStatusEnum } from '@/helpers/constants/enumerations.js'
    import CommonDialog from '@/components/controls/CommonDialog'
    //import QrcodeVue from 'qrcode.vue'
    import domtoimage from 'dom-to-image'
    import { useStripHtml } from '@/helpers/common.js'
    import { copyToClipboard } from '@/helpers/clipboard.js'
    import ExcelEditor from '@/components/controls/ExcelEditor.vue'

    export default {
        components: {
            CommonDialog,/*, QrcodeVue,*/ ManageQrCode, ExcelEditor
        },

        data() {
            return {
                search: '',
                selectedSurveyId: 0,
                loading: false,
                enabled: false,
                isShared: null,
                isSharedQrForm: null,
                isPublished: null,
                active: null,
                showOnHomePage: null,
                surveys: [],
                mainSurveyList: [],
                userPermissionsList: [],
                showExcelEditor: false,
                headers: [
                    { text: this.$t('Id'), value: 'id' },
                    { text: this.$t('NameAr'), value: 'nameAr' },
                    //{ text: this.$t('NameEn'), value: 'nameEn' },
                    { text: this.$t('Published'), value: 'isPublished', align: 'center' },
                    { text: this.$t('ShowOnHomePage'), value: 'showOnHomePage', align: 'center' },
                    { text: this.$t('MediaCenter'), value: 'hideFromMediaCenter', align: 'center' },
                    { text: this.$t('HasTrickyQuestion'), value: 'hasTrickyQuestion', align: 'center' },
                    { text: this.$t('Active'), value: 'expired' },
                    { text: this.$t('IsShared'), value: 'isShared' },
                    { text: this.$t('CreatedBy'), value: 'createdByName' },
                    { text: this.$t('StartDate'), value: 'startDate' },
                    { text: this.$t('ExpiryDate'), value: 'expiryDate' },
                    { text: this.$t('Status'), value: 'status' },
                    { text: this.$t('Actions'), value: 'actions' },
                    { text: '', value: 'menu' },

                ],
                selectedItem: null,
                draft: SurveyStatusEnum.Draft,
                approved: SurveyStatusEnum.Approved,
                qrCodeDialog: false,
                surveyURL: '',
                selectedTitle: ''

            }
        },

        created() {
            this.listSruveys()
        },
        mounted() {
            this.hasUpdatePermission()
        },
        computed: {
            ...mapGetters('user', ['language']),
            getLanguage() {
                return this.language == null ? 'ar' : this.language

            },
            filteredList() {
                return this.surveys.filter(task => (this.showOnHomePage == null || this.showOnHomePage == false || task.showOnHomePage == this.showOnHomePage)
                    && (this.isShared == null || task.isShared == this.isShared || this.isShared == false)
                    && (this.isPublished == null || task.isPublished == this.isPublished || this.isPublished == false)
                    && (this.active == null || task.expired != this.active || this.active == false)
                )
            }
        },
        methods: {
            downloadQR() {
                // Reference to the qrcode-vue component
                const qrcodeEl = this.$refs.qrcode.$el

                // Convert the SVG element to a data URL
                domtoimage.toPng(qrcodeEl)
                    .then(dataUrl => {
                        // Create a link element
                        const link = document.createElement('a')
                        link.href = dataUrl
                        link.download = 'qrcode.png'

                        // Simulate click on the link to trigger download
                        link.click()
                    })
                    .catch(error => {
                        console.error('Error while converting QR code to image:', error)
                    })
            },
            toggleMediaCenter(item) {
                const hidden = !item.hideFromMediaCenter
                this.loading = true
                SurveyService.setHideFromMediaCenter(item.id, hidden).then((res) => {
                    if (res.success) {
                        item.hideFromMediaCenter = hidden
                        this.$root.$notification(this.$t(hidden ? 'HideFromMediaCenter' : 'ShowInMediaCenter'), 'success')
                    } else {
                        this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                    }
                }).catch(() => {
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                }).finally(() => {
                    this.loading = false
                })
            },
            shareSurvey(value) {
                this.loading = true
                SurveyService.shareSurvey(this.selectedSurveyId, value).then((res) => {
                    this.listSruveys()
                }).catch(() => {
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                }).finally(() => {
                    this.loading = false
                })
            },
            getTitle(question) {
                return this.language == 'ar' || this.language == null ? question.nameAr : question.nameEn
            },
            hasUpdatePermission() {
                UsersService.listCurrentUserPermissions().then((res) => {
                    if (res.data) {
                        this.userPermissionsList = res.data
                    }
                }).catch(() => {
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                }).finally(() => {
                    this.loading = false
                })
            },
            handleAction(action, item) {
                switch (action.action) {
                    case 'edit':
                        this.editSurvey(item.id)
                        break
                    case 'delete':
                        this.removeSurvey(item.id)
                        break
                    case 'results':
                        this.resultsSurvey(item.id)
                        break
                    case 'view':
                        this.editSurvey(item.id)
                        break
                    case 'copy':
                        this.copy(item.id)
                        break
                    case 'copyHtml':
                        this.generateHtml(item.id)
                        break
                    case 'analytics':
                        this.surveyAnalytics(item.id)
                        break
                    case 'qrCode':
                        this.genrateQrCode(item.id)
                        break
                    case 'makeCopy':
                        this.makeCopy(item.id)
                        break
                    default:
                        break
                }
            },
            editSurvey(id) {
                this.$router.push({ name: 'add-survey', query: { id } })
            },
            resultsSurvey(id) {
                this.$router.push({ name: 'survey-results', query: { id } })
            },
            surveyAnalytics(id) {
                this.$router.push({ name: 'survey-analytics', query: { id } })
            },

            listSruveys() {
                this.loading = true
                SurveyService.listSurveys(false).then((res) => {
                    this.surveys = res.data
                }).catch(() => {
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                }).finally(() => {
                    this.loading = false
                })
            },

            // copyToClipboard falls back to execCommand when navigator.clipboard is unavailable
            // (plain HTTP on a LAN IP is not a secure context, so the API is missing there)
            async copy(itemId) {
                const link = window.location.protocol + '//' + window.location.hostname + ':' + window.location.port + `/form/${itemId}`
                const ok = await copyToClipboard(link)
                this.$root.$notification(this.$t(ok ? 'CopySucceeded' : 'ErrorOccurred'), ok ? 'success' : 'error')
            },
            async generateHtml(itemId) {
                var url = window.location.protocol + '//' + window.location.hostname + ':' + window.location.port + `/form/${itemId}`
                var html = `<iframe id="myIframe" src = '${url}' > </iframe>`
                const ok = await copyToClipboard(html)
                this.$root.$notification(this.$t(ok ? 'CopySucceeded' : 'ErrorOccurred'), ok ? 'success' : 'error')
            },
            genrateQrCode(itemId, isShared, title) {
                this.selectedSurveyId = itemId
               // this.isShared = isShared
                this.surveyURL = window.location.protocol + '//' + window.location.hostname + ':' + window.location.port + `/form/${itemId}`
                this.qrCodeDialog = true
                this.isSharedQrForm = isShared
                this.selectedTitle = title
            },

            async removeSurvey(id) {
                if (await this.$root.$confirm(this.$t('Delete'), this.$t('ConfirmDelete'))) {
                    this.loading = true
                    SurveyService.deleteSurvey(id)
                        .then((res) => {
                            this.listSruveys()
                        })
                        .catch(() => {
                            this.$root.$notification(this.$t('SurveyInProcess'), 'error')
                        }).finally(() => this.loading = false)
                }
            },
            exportToEdit(surveyId) {
                this.selectedSurveyId = surveyId
                this.showExcelEditor = true
            },
            async makeCopy(surveyId) {

                const confirmResult = await this.$root.$confirm(this.$t('MakeCopy'), this.$t('Are you sure you want to make a copy of this Survey ?'), { checkboxLabel: this.$t('CopySurveyLogicOption') })
                if (confirmResult.confirmed) {
                    const copyLogic = confirmResult.checkbox
                    this.loading = true
                    SurveyService.loadSurveywithoutAuth(surveyId).then((res) => {
                        this.survey = res.data
                        this.survey.id = null
                        this.survey.isPublished = false
                        SurveyService.copySurvey(this.survey, copyLogic).then((res) => {
                            this.$router.push('/add-survey?id=' + res.data)
                        }).catch(() => {
                            this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                        }).finally(() => {
                            this.loading = false
                        })
                    }).catch(() => {
                        this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                    }).finally(() => {
                        this.loading = false
                    })

                }
            },
            RemoveHtmlTags(text) {
                return useStripHtml(text)
            },

            filterGrid(e) {

                if (e) {
                    this.mainSurveyList = this.surveys
                    switch (e.value) {
                        case 'isShared':
                            this.surveys = this.surveys.filter(x => x.isShared == true)
                            break
                        case 'showOnHomePage':
                            this.surveys = this.surveys.filter(x => x.showOnHomePage == true)
                            break
                        case 'isPublished':
                            this.surveys = this.surveys.filter(x => x.isPublished == true)
                            break
                        case 'active':
                            this.surveys = this.surveys.filter(x => x.expired == false)
                            break
                        default:
                    }
                }
                else {
                    this.surveys = this.mainSurveyList
                }
            }
        }
    }
</script>

<style scoped>
    .sl-page {
        --sl-surface: #f3f6f5;
        --sl-line: #e6ecea;
        --sl-text: #1f2a28;
        --sl-muted: #6b7a77;
        --sl-primary: #0a716e;
        --sl-primary-tint: #e6f2f1;
        --sl-amber: #b45309;
        --sl-amber-tint: #fdf3e7;
        --sl-danger: #b91c1c;
        --sl-danger-tint: #fee2e2;
        --sl-ease: cubic-bezier(.2, .8, .2, 1);
        padding: 22px 20px 32px;
        background: #fff;
    }

    /* ---------- header ---------- */
    .sl-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-wrap: wrap;
        gap: 14px;
        margin-bottom: 16px;
    }
    .sl-title-wrap { display: flex; align-items: center; gap: 12px; }
    .sl-title-ic {
        display: inline-flex; align-items: center; justify-content: center;
        width: 42px; height: 42px; border-radius: 13px; background: var(--sl-primary-tint);
    }
    .sl-title-ic .v-icon { color: var(--sl-primary) !important; }
    .sl-title { margin: 0; font-size: 22px; font-weight: 800; line-height: 1.2; color: var(--sl-text); }
    .sl-count {
        display: inline-flex; align-items: center; justify-content: center;
        min-width: 30px; height: 26px; padding: 0 9px; border-radius: 999px;
        background: var(--sl-primary); color: #fff; font-size: 12.5px; font-weight: 700;
    }
    .sl-add {
        border-radius: 12px !important; height: 42px !important; padding: 0 18px !important;
        font-weight: 700; letter-spacing: 0; text-transform: none;
        box-shadow: 0 8px 18px -10px var(--sl-primary) !important;
    }

    /* ---------- card + toolbar ---------- */
    .sl-card {
        border: 1px solid var(--sl-line) !important;
        border-radius: 18px !important;
        box-shadow: 0 1px 2px rgba(15, 23, 42, .04), 0 8px 24px -16px rgba(15, 23, 42, .18) !important;
        overflow: hidden;
    }
    .sl-toolbar {
        display: flex; align-items: center; flex-wrap: wrap; gap: 12px;
        padding: 12px 14px; border-bottom: 1px solid var(--sl-line);
    }
    .sl-search {
        display: flex; align-items: center; gap: 8px;
        flex: 1 1 260px; max-width: 380px; height: 40px; padding: 0 12px;
        border: 1px solid var(--sl-line); border-radius: 12px; background: var(--sl-surface);
        transition: border-color .2s var(--sl-ease), background-color .2s var(--sl-ease), box-shadow .2s var(--sl-ease);
    }
    .sl-search:focus-within { background: #fff; border-color: var(--sl-primary); box-shadow: 0 0 0 3px var(--sl-primary-tint); }
    .sl-search-ic { color: var(--sl-muted) !important; }
    .sl-search-input { flex: 1 1 auto; min-width: 0; border: 0; outline: none; background: transparent; font: inherit; font-size: 13.5px; color: var(--sl-text); }
    .sl-search-input::placeholder { color: var(--sl-muted); }
    .sl-search-input::-webkit-search-cancel-button { -webkit-appearance: none; }

    .sl-filters { display: flex; flex-wrap: wrap; gap: 8px; margin-inline-start: auto; }
    .sl-filter {
        display: inline-flex; align-items: center; gap: 6px;
        height: 34px; padding: 0 12px;
        border: 1px solid var(--sl-line); border-radius: 999px; background: #fff;
        color: var(--sl-muted); font: inherit; font-size: 12.5px; font-weight: 600; cursor: pointer;
        transition: background-color .2s var(--sl-ease), border-color .2s var(--sl-ease), color .2s var(--sl-ease);
    }
    .sl-filter-ic { color: inherit !important; }
    .sl-filter:hover { background: var(--sl-surface); }
    .sl-filter:focus { outline: none; }
    .sl-filter:focus-visible { box-shadow: 0 0 0 2px #fff, 0 0 0 4px var(--sl-primary); }
    .sl-filter.is-on { border-color: var(--sl-primary); color: var(--sl-primary); background: var(--sl-primary-tint); }

    /* ---------- table ---------- */
    .sl-table ::v-deep thead th {
        height: 46px !important; font-size: 12px !important; font-weight: 700 !important;
        color: var(--sl-muted) !important; background: #fafcfb;
        border-bottom: 1px solid var(--sl-line) !important; white-space: nowrap;
    }
    .sl-table ::v-deep tbody td {
        height: 56px !important; font-size: 13.5px; color: var(--sl-text);
        border-bottom: 1px solid #eef2f1 !important;
    }
    .sl-table ::v-deep tbody tr { cursor: pointer; transition: background-color .15s var(--sl-ease); }
    .sl-table ::v-deep tbody tr:hover { background: var(--sl-surface) !important; }
    .sl-table ::v-deep tbody tr:last-child td { border-bottom: 0 !important; }
    .sl-table ::v-deep .v-data-footer { border-top: 1px solid var(--sl-line); font-size: 12.5px; color: var(--sl-muted); padding: 4px 8px; }

    .sl-id {
        display: inline-block; padding: 2px 8px; border-radius: 8px;
        background: var(--sl-surface); color: var(--sl-muted); font-size: 12px; font-weight: 700; font-variant-numeric: tabular-nums;
    }
    .sl-name { font-weight: 700; color: var(--sl-text); line-height: 1.45; }
    .sl-muted { color: var(--sl-muted); font-size: 12.5px; }
    .sl-date { color: var(--sl-muted); font-size: 12.5px; font-variant-numeric: tabular-nums; white-space: nowrap; }

    .sl-flag {
        display: inline-flex; align-items: center; justify-content: center;
        width: 24px; height: 24px; border-radius: 50%;
    }
    .sl-flag.is-on { background: var(--sl-primary-tint); }
    .sl-flag.is-on .v-icon { color: var(--sl-primary) !important; }
    .sl-flag.is-off { background: #f0f3f2; }
    .sl-flag.is-off .v-icon { color: #b8c4c1 !important; }
    .sl-flag.is-warn { background: var(--sl-amber-tint); }
    .sl-flag.is-warn .v-icon { color: var(--sl-amber) !important; }

    .sl-status {
        display: inline-flex; align-items: center; gap: 7px; height: 26px; padding: 0 10px;
        border-radius: 999px; font-size: 12px; font-weight: 700; white-space: nowrap;
        background: var(--sl-surface); color: var(--sl-muted);
    }
    .sl-status-dot { width: 7px; height: 7px; border-radius: 50%; background: currentColor; }
    .sl-status--approved { background: var(--sl-primary-tint); color: var(--sl-primary); }
    .sl-status--pending { background: var(--sl-amber-tint); color: var(--sl-amber); }
    .sl-status--draft { background: #eef1f0; color: #5c6b69; }

    .sl-actions { display: inline-flex; align-items: center; gap: 4px; white-space: nowrap; }
    .sl-action {
        display: inline-flex; align-items: center; justify-content: center;
        width: 32px; height: 32px; border: 1px solid transparent; border-radius: 9px;
        background: transparent; text-decoration: none; cursor: pointer; padding: 0;
        transition: background-color .15s var(--sl-ease), border-color .15s var(--sl-ease);
    }
    .sl-action .v-icon { color: var(--sl-muted) !important; transition: color .15s var(--sl-ease); }
    .sl-action:hover { background: var(--sl-primary-tint); }
    .sl-action:hover .v-icon { color: var(--sl-primary) !important; }
    .sl-action:focus { outline: none; }
    .sl-action:focus-visible { box-shadow: 0 0 0 2px #fff, 0 0 0 4px var(--sl-primary); }
    .sl-action--primary .v-icon { color: var(--sl-primary) !important; }
    .sl-action--danger:hover { background: var(--sl-danger-tint); }
    .sl-action--danger:hover .v-icon { color: var(--sl-danger) !important; }

    .sl-empty {
        display: flex; flex-direction: column; align-items: center; gap: 8px;
        padding: 36px 12px; color: var(--sl-muted); font-size: 13.5px; font-weight: 600;
    }

    @media (max-width: 600px) {
        .sl-page { padding: 14px 12px 24px; }
        .sl-title { font-size: 19px; }
        .sl-search { max-width: none; }
        .sl-filters { margin-inline-start: 0; }
    }
    @media (prefers-reduced-motion: reduce) {
        .sl-filter, .sl-search, .sl-action, .sl-table ::v-deep tbody tr { transition: none; }
    }
</style>
